import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { isEmpty, isEqual, isNull, size } from "lodash";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey, red } from "@mui/material/colors";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import planVersionService from "@plannel/services/data/plan-version-service";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";

const { versionStatusCode, module, limitVersion } = codeUtil;
const { RELEASED } = versionStatusCode;
const { MP } = module;
const REDUX_MP_VERSION_FILTER = "MP_VERSION_FILTER";
const MpVersionFilter = ({ viewName, mpVersionValue, onChangeFilter, statusFilters = [] }) => {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const [mpVersionFilterDataList, setMpVersionFilterDataList] = useState([]);

  const onChangeHandler = (_, mpVersionValue) => {
    onChangeFilter(mpVersionValue);

    const createMpVersionReduxState = reduxUtil.createObjectState(viewName, { mpVersion: mpVersionValue });
    reduxDispatch(reduxUtil.updateViewState(createMpVersionReduxState));
  }

  /* Version 정렬이 필요한 경우 사용. */
  const sortMpVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }

    const list = [...basicVersions, ...executedVersions, ...releaseVersions]
    if (size(list) > limitVersion) {
      const limitList = list.slice(0, limitVersion)
      return limitList;
    }
    return list;
  }
  
  /* MP Version Status Filter [PLANNED, RELEASED, UNPLANNED] default: ALL */
  const mpVersionDataStatusFilter = (results) => {
    let resultsFilter = [];

    if (!isEmpty(statusFilters)) {
      for (const result of results) {
        if (statusFilters.includes(result.statusCd)) {
          resultsFilter.push(result);
        }
      }
    } else {
      resultsFilter = results;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    const existSelectedValueInResults = resultsFilter.find(result => isEqual(result.id, mpVersionValue?.id));

    /* Defualt Classification Setting */
    if (!isEmpty(resultsFilter)) {
      if (isEmpty(existSelectedValueInResults)) {
        onChangeHandler(null, resultsFilter[0]);
      } else {
        onChangeHandler(null, existSelectedValueInResults);
      }
    }

    return sortMpVersions(resultsFilter);
  }

  const getMpVersionData = () => {
      let mpVersionResultData = [];

      const params = { moduleCd: MP, orderByColumn: "updatedTs", sortType: "desc" };
      planVersionService.getAll(params)
        .then(response => {
          const { results } = response.data;
          if (!isEmpty(results)) {
            mpVersionResultData = results;
          }

          const mpVersionFilterData = mpVersionDataStatusFilter(mpVersionResultData);
          setMpVersionFilterDataList(mpVersionFilterData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createMpVersionDataListReduxState = reduxUtil.createObjectState(REDUX_MP_VERSION_FILTER, { mpVersion: mpVersionResultData });
          reduxDispatch(reduxUtil.updateViewState(createMpVersionDataListReduxState));
        })
  }

  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }

      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }

    return null;
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  useEffect(() => {
    getMpVersionData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <VersionAutocomplete
      options={mpVersionFilterDataList}
      value={mpVersionValue}
      onChange={onChangeHandler}
      label={t("version")}
      getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
      getIconColor={iconColor}
      getTooltip={setToolTip}
    />
  )
}

const reloadMpVersionFilter = (reduxDispatch) => {
  const mpVersionReduxFilterState = reduxUtil.getViewState(REDUX_MP_VERSION_FILTER);

  reduxDispatch(reduxUtil.removeViewState(mpVersionReduxFilterState));
}

export default React.memo(MpVersionFilter);
export { reloadMpVersionFilter };